import {
  Button,
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  Label,
  useToast,
} from '@ais-ui/ui-core';
import { Job, useAbortJob } from '@ais-ui/websockets';
import React, { useState } from 'react';
import { useLocalization } from '@ais-ui/locale-management';

interface AbortReportProps {
  showDialog: boolean;
  setShowDialog: React.Dispatch<React.SetStateAction<boolean>>;
  job: Job;
  onSuccess?: () => void;
}

export const AbortReport = ({
  showDialog,
  setShowDialog,
  job,
  onSuccess,
}: AbortReportProps) => {
  const { messages } = useLocalization();
  const [isSubmitting, setIsSubmitting] = useState(false);
  const { mutate } = useAbortJob();
  const { toast } = useToast();

  const onAbort = (event: React.FormEvent) => {
    event.preventDefault();
    setIsSubmitting(true);
    const reportName = job.name;

    if (!job.uuid) {
      toast({
        className: 'abort-report-error-toast',
        title: messages.genericToastTitleError,
        description: `Could not abort report "${reportName}": missing job ID`,
        variant: 'destructive',
      });
      setIsSubmitting(false);
      setShowDialog(false);
      return;
    }

    mutate(job.uuid, {
      onSuccess: () => {
        toast({
          className: 'abort-report-success-toast',
          title: messages.genericToastTitleSuccess,
          description: `Report "${reportName}" has been aborted`,
        });
        setIsSubmitting(false);
        setShowDialog(false);
        onSuccess?.();
      },
      onError: (error: any) => {
        if (error.response?.status === 403) {
          toast({
            className: 'abort-report-error-toast',
            title: messages.genericToastTitleForbidden,
            description: `You do not have permission to abort report "${reportName}"`,
            variant: 'destructive',
          });
        } else {
          toast({
            className: 'abort-report-error-toast',
            title: messages.genericToastTitleError,
            description: `Failed to abort report "${reportName}"`,
            variant: 'destructive',
          });
        }
        setIsSubmitting(false);
        setShowDialog(false);
      },
    });
  };

  return (
    <Dialog open={showDialog} onOpenChange={setShowDialog}>
      <DialogContent id="abort-report-dialog">
        <DialogHeader className="space-y-4">
          <DialogTitle>Abort Report</DialogTitle>
          <DialogDescription>
            Are you sure you want to abort this report? This will stop the
            report processing.
          </DialogDescription>
        </DialogHeader>
        <form onSubmit={onAbort}>
          <div className="grid grid-cols-[auto_minmax(50%,_1fr)] gap-y-4 gap-x-2 items-baseline">
            <Label className="font-light">Report name</Label>
            <Label htmlFor="report-name">{job.name}</Label>
          </div>
          <DialogFooter className="p-4">
            <Button
              onClick={(event) => onAbort(event)}
              variant="destructive"
              id="report-confirm-abort-button"
              type="submit"
              disabled={isSubmitting}
            >
              {isSubmitting ? messages.genericSubmitting : 'Abort'}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
};
